import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Zap, Truck } from "lucide-react";
import { COMPANY } from "../../data/site";
import { MaskLines } from "./Motion";

const RotatingBadge = () => (
  <motion.svg viewBox="0 0 200 200" className="w-28 h-28 md:w-36 md:h-36" animate={{ rotate: 360 }} transition={{ duration: 22, repeat: Infinity, ease: "linear" }} aria-hidden="true">
    <defs><path id="circ" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
    <circle cx="100" cy="100" r="99" fill="#D4E86A" />
    <text fontSize="14" fontWeight="700" letterSpacing="2.6" fill="#0F2A19" fontFamily="Satoshi, sans-serif">
      <textPath href="#circ">EFFICACE À 99 % • ACTION RAPIDE • GIRONDE •</textPath>
    </text>
    <text x="100" y="116" textAnchor="middle" fontSize="46" fontWeight="800" fill="#0F2A19" fontFamily="Cabinet Grotesk, sans-serif">99%</text>
  </motion.svg>
);

export const HomeHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} className="relative pt-24 px-3 md:px-6" data-testid="home-hero">
      <div className="relative overflow-hidden rounded-[32px] bg-forest text-cream grain min-h-[calc(100svh-7rem)] flex items-end">
        <motion.picture style={{ y: bgY }} className="absolute inset-0 -top-[10%] h-[120%] block">
          <source media="(max-width: 767px)" srcSet="/images/hero-brume-mobile.webp" />
          <img src="/images/hero-brume.webp" alt="Brume de traitement anti-moustique dans un jardin de Gironde" fetchpriority="high" className="w-full h-full object-cover opacity-70" />
        </motion.picture>
        <div className="absolute inset-0 bg-gradient-to-tr from-forest via-forest/70 to-forest/10" />

        <motion.div style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }} initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:block absolute right-[6%] top-[14%] w-[300px] xl:w-[340px] z-[2]">
          <div className="clip-arch overflow-hidden aspect-[3/4] border border-cream/20 shadow-2xl shadow-black/40">
            <img src="/images/moustique-goutte.webp" alt="Moustique tigre en gros plan" className="w-full h-full object-cover scale-110" />
          </div>
          <div className="absolute -left-14 -top-6"><RotatingBadge /></div>
        </motion.div>

        <motion.div style={{ y: textY }} className="relative z-[3] w-full max-w-[1400px] mx-auto px-6 lg:px-12 pb-10 md:pb-14 pt-28">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="eyebrow text-lime">Termite Habitat 33 · Spécialiste anti-moustique</motion.p>
          <h1 className="mt-6 font-display font-extrabold text-[11.5vw] sm:text-7xl lg:text-[7.4rem] leading-[0.88] tracking-[-0.045em] max-w-5xl" data-testid="home-title">
            <MaskLines lines={["Anti-moustique", <span key="b" className="serif-i text-lime font-normal">à Bordeaux</span>, "& en Gironde."]} delay={0.2} />
          </h1>
          <div className="mt-10 grid lg:grid-cols-[1.1fr_1fr] gap-8 items-end">
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }} className="lead-text text-cream/85 max-w-xl">
              Reprenez votre jardin. Nous éliminons les sources de larves et traitons tout votre extérieur par nébulisation et atomiseur avec des biocides professionnels : un traitement choc, efficace à 99 %, déplacement inclus.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="flex flex-wrap gap-3 lg:justify-end">
              <Link to="/contact" className="btn btn-lime !px-7 !py-4" data-testid="home-hero-quote">Devis gratuit<ArrowRight className="w-4 h-4" /></Link>
              <a href={COMPANY.phoneHref} className="btn btn-ghost-light !px-7 !py-4" data-testid="home-hero-call"><Phone className="w-4 h-4" />{COMPANY.phone}</a>
            </motion.div>
          </div>
          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }} className="mt-10 pt-6 border-t border-cream/15 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            {[[Zap, "Action rapide, effet choc"], [ShieldCheck, "Biocides professionnels"], [Truck, "Déplacement inclus en Gironde"]].map(([I, t]) => (
              <li key={t} className="flex items-center gap-3 text-cream/90"><span className="w-9 h-9 rounded-full bg-cream/10 backdrop-blur grid place-items-center"><I className="w-4 h-4 text-lime" /></span>{t}</li>
            ))}
          </motion.ul>
        </motion.div>
      </div>
    </section>
  );
};
